import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from '@fluent/ui';
import type { ExamDialogue, ExamGrammar, ExamMatching, ExamReading, ExamVocabulary } from '@fluent/types';
import type { TaskAnswers } from './attempt.js';
import {
  GapText,
  Glossary,
  LETTERS,
  McqQuestion,
  OptionSheet,
  ReviewNote,
  TaskHeader,
  paragraphsOf,
} from './parts.js';

/** Tasks 2–6. Task 1 (listening) has its own file. */

interface TaskProps<T> {
  task: T;
  answers: TaskAnswers;
  onPick: (index: number, letter: string) => void;
  onClear: (index: number) => void;
  review: boolean;
}

/** Which gap (1-based) currently holds `letter`, for shared-pool tasks. */
function gapHolding(answers: TaskAnswers, letter: string): number | undefined {
  const hit = Object.entries(answers).find(([, v]) => v === letter);
  return hit ? Number(hit[0]) + 1 : undefined;
}

/* ------------------------------ Task 2 ------------------------------ */

export function MatchingTask({ task, answers, onPick, review }: TaskProps<ExamMatching>) {
  const letters = LETTERS.slice(0, 6);
  return (
    <div className="fluent-exam__task">
      <TaskHeader n={2} />
      <article className="fluent-exam__passage" lang="en">
        <h3 className="fluent-exam__passage-title">{task.title}</h3>
        {task.paragraphs.map((p, i) => (
          <p key={i} className="fluent-exam__para">
            <span className="fluent-exam__para-letter" aria-hidden="true">
              {letters[i]}
            </span>
            <span className="fluent-exam__sr">{letters[i]}. </span>
            {p}
          </p>
        ))}
      </article>
      <Glossary items={task.glossary} />

      {task.questions.map((q, i) => {
        const picked = answers[i];
        return (
          <section key={i} className="fluent-exam__q" data-review={review || undefined}>
            <p className="fluent-exam__q-text" lang="en">
              <span className="fluent-exam__q-num">{i + 1}</span>
              {q.question}
            </p>
            <div className="fluent-exam__letters" role="radiogroup" aria-label={q.question}>
              {letters.map((l) => (
                <Pressable
                  key={l}
                  className="fluent-exam__letter"
                  role="radio"
                  aria-checked={picked === l}
                  data-selected={picked === l || undefined}
                  data-state={review ? (l === q.answer ? 'right' : l === picked ? 'wrong' : undefined) : undefined}
                  onClick={() => !review && onPick(i, l)}
                >
                  {l}
                </Pressable>
              ))}
            </div>
            {review && <ReviewNote picked={picked} correct={q.answer} evidence={q.evidence} />}
          </section>
        );
      })}
    </div>
  );
}

/* ------------------------------ Task 3 ------------------------------ */

export function ReadingTask({ task, answers, onPick, review }: TaskProps<ExamReading>) {
  return (
    <div className="fluent-exam__task">
      <TaskHeader n={3} />
      <article className="fluent-exam__passage" lang="en">
        {task.title && <h3 className="fluent-exam__passage-title">{task.title}</h3>}
        {task.intro && <p className="fluent-exam__passage-intro">{task.intro}</p>}
        {paragraphsOf(task.text).map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </article>
      <Glossary items={task.glossary} />

      {task.questions.map((q, i) => (
        <McqQuestion
          key={i}
          number={i + 1}
          question={q.question}
          options={q.options}
          picked={answers[i]}
          onPick={(letter) => onPick(i, letter)}
          review={review}
          correct={q.answer}
          evidence={q.evidence}
        />
      ))}
    </div>
  );
}

/* ------------------------------ Task 4 ------------------------------ */

export function VocabularyTask({ task, answers, onPick, onClear, review }: TaskProps<ExamVocabulary>) {
  const { t } = useTranslation();
  const [gap, setGap] = useState<number | null>(null);
  const word = (letter: string | undefined) =>
    letter === undefined ? undefined : task.words[LETTERS.indexOf(letter as (typeof LETTERS)[number])];

  return (
    <div className="fluent-exam__task">
      <TaskHeader n={4} />

      <div className="fluent-exam__bank" aria-label={t('exam.word_bank')}>
        {task.words.map((w, i) => {
          const letter = LETTERS[i] ?? '';
          const heldBy = gapHolding(answers, letter);
          return (
            <span key={w} className="fluent-exam__bank-word" data-used={heldBy !== undefined || undefined} lang="en">
              <b>{letter}</b> <span className="fluent-exam__bank-text">{w}</span>
              {heldBy !== undefined && <span className="fluent-exam__bank-gap">{heldBy}</span>}
            </span>
          );
        })}
      </div>

      <article className="fluent-exam__passage-wrap">
        <h3 className="fluent-exam__passage-title" lang="en">
          {task.title}
        </h3>
        <GapText
          text={task.text}
          fill={(n) => word(answers[n - 1])}
          onGap={(n) => !review && setGap(n)}
          gapState={
            review ? (n) => (answers[n - 1] === task.answers[n - 1] ? 'right' : 'wrong') : undefined
          }
          placeholder={review ? '—' : undefined}
        />
      </article>

      {review && (
        <ol className="fluent-exam__review-list">
          {task.answers.map((correct, i) => (
            <li key={i}>
              <ReviewNote
                picked={answers[i]}
                correct={correct}
                label={(l) => `${l} · ${word(l) ?? ''}`}
              />
            </li>
          ))}
        </ol>
      )}

      {gap !== null && (
        <OptionSheet
          open
          onOpenChange={(open) => !open && setGap(null)}
          gap={gap}
          options={task.words}
          picked={answers[gap - 1]}
          usedIn={(l) => gapHolding(answers, l)}
          onPick={(l) => {
            onPick(gap - 1, l);
            setGap(null);
          }}
          onClear={() => {
            onClear(gap - 1);
            setGap(null);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------ Task 5 ------------------------------ */

export function GrammarTask({ task, answers, onPick, onClear, review }: TaskProps<ExamGrammar>) {
  const [gap, setGap] = useState<number | null>(null);
  const optionText = (n: number, letter: string | undefined) =>
    letter === undefined ? undefined : task.gaps[n - 1]?.options[LETTERS.indexOf(letter as (typeof LETTERS)[number])];

  return (
    <div className="fluent-exam__task">
      <TaskHeader n={5} />
      <article className="fluent-exam__passage-wrap">
        <h3 className="fluent-exam__passage-title" lang="en">
          {task.title}
        </h3>
        <GapText
          text={task.text}
          fill={(n) => optionText(n, answers[n - 1])}
          onGap={(n) => !review && setGap(n)}
          gapState={
            review ? (n) => (answers[n - 1] === task.gaps[n - 1]?.answer ? 'right' : 'wrong') : undefined
          }
          placeholder={review ? '—' : undefined}
        />
      </article>

      {review && (
        <ol className="fluent-exam__review-list">
          {task.gaps.map((g, i) => (
            <li key={i}>
              <ReviewNote
                picked={answers[i]}
                correct={g.answer}
                why={g.why_ka}
                label={(l) => `${l} · ${optionText(i + 1, l) ?? ''}`}
              />
            </li>
          ))}
        </ol>
      )}

      {gap !== null && (
        <OptionSheet
          open
          onOpenChange={(open) => !open && setGap(null)}
          gap={gap}
          options={task.gaps[gap - 1]?.options ?? []}
          picked={answers[gap - 1]}
          onPick={(l) => {
            onPick(gap - 1, l);
            setGap(null);
          }}
          onClear={() => {
            onClear(gap - 1);
            setGap(null);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------ Task 6 ------------------------------ */

export function DialogueTask({ task, answers, onPick, onClear, review }: TaskProps<ExamDialogue>) {
  const { t } = useTranslation();
  const [gap, setGap] = useState<number | null>(null);
  const line = (letter: string | undefined) =>
    letter === undefined ? undefined : task.options[LETTERS.indexOf(letter as (typeof LETTERS)[number])];

  return (
    <div className="fluent-exam__task">
      <TaskHeader n={6} />

      <div className="fluent-exam__bank fluent-exam__bank--lines" aria-label={t('exam.sentences')}>
        {task.options.map((o, i) => {
          const letter = LETTERS[i] ?? '';
          const heldBy = gapHolding(answers, letter);
          return (
            <p key={letter} className="fluent-exam__bank-line" data-used={heldBy !== undefined || undefined} lang="en">
              <b>{letter}</b> <span className="fluent-exam__bank-text">{o}</span>
              {heldBy !== undefined && <span className="fluent-exam__bank-gap">{heldBy}</span>}
            </p>
          );
        })}
      </div>

      <article className="fluent-exam__chat" lang="en">
        <h3 className="fluent-exam__passage-title">{task.title}</h3>
        {task.lines.map((l, i) => {
          const who = task.speakers[l.speaker];
          if ('text' in l) {
            return (
              <p key={i} className="fluent-exam__chat-line" data-side={l.speaker}>
                <span className="fluent-exam__chat-who">{who}</span>
                {l.text}
              </p>
            );
          }
          const n = l.gap;
          const picked = answers[n - 1];
          const state = review ? (picked === task.answers[n - 1] ? 'right' : 'wrong') : undefined;
          return (
            <div key={i} className="fluent-exam__chat-line" data-side={l.speaker}>
              <span className="fluent-exam__chat-who">{who}</span>
              <Pressable
                className="fluent-exam__gap fluent-exam__gap--line"
                data-filled={picked !== undefined || undefined}
                data-state={state}
                onClick={() => !review && setGap(n)}
                aria-label={t('exam.gap_aria', { n })}
              >
                <span className="fluent-exam__gap-num">{n}</span>
                <span className="fluent-exam__gap-value">{line(picked) ?? (review ? '—' : t('exam.gap_empty'))}</span>
              </Pressable>
              {review && picked !== task.answers[n - 1] && (
                <ReviewNote
                  picked={picked}
                  correct={task.answers[n - 1]}
                  label={(x) => `${x} · ${line(x) ?? ''}`}
                />
              )}
            </div>
          );
        })}
      </article>

      {gap !== null && (
        <OptionSheet
          open
          onOpenChange={(open) => !open && setGap(null)}
          gap={gap}
          options={task.options}
          picked={answers[gap - 1]}
          usedIn={(l) => gapHolding(answers, l)}
          onPick={(l) => {
            onPick(gap - 1, l);
            setGap(null);
          }}
          onClear={() => {
            onClear(gap - 1);
            setGap(null);
          }}
        />
      )}
    </div>
  );
}
